import { V2 } from '../theme/tokens'
import React, { useMemo, useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { formatInt, formatMonthDay, formatMonthNameYear, formatShortDate } from '../utils/format';

const DAY_MS = 24 * 60 * 60 * 1000;

const parseDateValue = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === 'number' && Number.isFinite(value)) {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d;
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
};

// 🔴 Die drei Zweige sind Tooltip-BESCHRIFTUNGEN, keine Achsen-Labels: der
// Sparkline ist ein 120×40-Mikrodiagramm ohne gerenderte Achse. Deshalb
// englische Namen (`Oct 2026`, `Oct 6`) und das kurze deutsche Datum — nicht
// die numerische Achsenform (W3).
const formatBucketLabel = (date, bucketDays) => {
  if (!(date instanceof Date)) return '';
  if (bucketDays >= 30) {
    return formatMonthNameYear(date);
  }
  if (bucketDays >= 7) {
    return `Week of ${formatMonthDay(date)}`;
  }
  return formatShortDate(date);
};

const aggregateByRange = (entries) => {
  if (entries.length <= 1) return entries;

  const timestamps = entries.map((e) => e.date.getTime());
  const minTs = Math.min(...timestamps);
  const maxTs = Math.max(...timestamps);
  const spanDays = (maxTs - minTs) / DAY_MS;

  let bucketDays = 1;
  if (spanDays > 90) bucketDays = 30;
  else if (spanDays > 30) bucketDays = 7;

  if (bucketDays === 1) return entries;

  const buckets = new Map();
  entries.forEach((entry) => {
    const ts = entry.date.getTime();
    let bucketKey;
    let bucketStart;

    if (bucketDays >= 30) {
      const year = entry.date.getUTCFullYear();
      const month = entry.date.getUTCMonth();
      bucketKey = `${year}-${month}`;
      bucketStart = new Date(Date.UTC(year, month, 1));
    } else {
      const index = Math.floor((ts - minTs) / (bucketDays * DAY_MS));
      bucketKey = index;
      bucketStart = new Date(minTs + index * bucketDays * DAY_MS);
    }

    const bucket = buckets.get(bucketKey) || {
      sum: 0,
      count: 0,
      date: bucketStart,
      label: formatBucketLabel(bucketStart, bucketDays),
    };
    bucket.sum += entry.y;
    bucket.count += 1;
    buckets.set(bucketKey, bucket);
  });

  return [...buckets.values()]
    .sort((a, b) => a.date - b.date)
    .map((bucket) => ({
      x: bucket.label,
      y: bucket.count ? bucket.sum / bucket.count : 0,
    }));
};

export default function Sparkline({
  data = [],
  width = 120,
  height = 40,
  stroke,
  fill,
  strokeWidth = 1.5,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const effectiveStroke = stroke ?? (isDark ? V2.info100 : V2.infoText);
  const effectiveFill = fill ?? (isDark ? 'rgba(144,202,249,0.12)' : 'rgba(30,64,175,0.07)');

  const [hoverIndex, setHoverIndex] = useState(null);

  const { pathD, areaD, points } = useMemo(() => {
    const initial = (Array.isArray(data) ? data : [])
      .map((value, idx) => {
        let y;
        let xLabel;
        if (typeof value === 'object' && value !== null) {
          y = Number(value.y);
          xLabel = value.x ?? idx + 1;
        } else {
          y = Number(value);
          xLabel = idx + 1;
        }
        if (!Number.isFinite(y)) return null;

        const date = parseDateValue(xLabel);
        return {
          x: xLabel,
          y,
          date,
        };
      })
      .filter(Boolean);

    if (!initial.length) return { pathD: '', areaD: '', points: [] };

    let normalized = initial.map(({ x, y }) => ({ x, y }));

    const hasDates = initial.every((entry) => entry.date instanceof Date);
    if (hasDates) {
      normalized = aggregateByRange(
        initial.map((entry) => ({ x: entry.x, y: entry.y, date: entry.date }))
      );
    }

    if (!normalized.length) return { pathD: '', areaD: '', points: [] };

    const ys = normalized.map((p) => p.y);
    let minY = Math.min(...ys);
    let maxY = Math.max(...ys);
    if (minY === maxY) {
      minY -= 1;
      maxY += 1;
    }

    const pad = 2;
    const W = Math.max(1, width - pad * 2);
    const H = Math.max(1, height - pad * 2);
    const calcX = (i) => pad + (i * W) / Math.max(1, normalized.length - 1);
    const calcY = (v) => pad + (H - ((v - minY) / (maxY - minY)) * H);

    let path = '';
    let started = false;
    normalized.forEach((pt, i) => {
      const cx = calcX(i);
      const cy = calcY(pt.y);
      path += started ? ` L ${cx} ${cy}` : `M ${cx} ${cy}`;
      started = true;
    });

    let area = '';
    if (path) {
      const coords = normalized.map((pt, i) => [calcX(i), calcY(pt.y)]);
      const [first] = coords;
      const last = coords[coords.length - 1];
      area =
        `M ${first[0]} ${height - pad} ` +
        coords.map(([cx, cy]) => `L ${cx} ${cy}`).join(' ') +
        ` L ${last[0]} ${height - pad} Z`;
    }

    const points = normalized.map((pt, i) => ({
      ...pt,
      cx: calcX(i),
      cy: calcY(pt.y),
    }));

    return { pathD: path, areaD: area, points };
  }, [data, width, height]);

  if (!pathD) {
    return (
      <Box
        sx={{
          width,
          height,
          borderRadius: 1,
          border: '1px dashed rgba(100,116,139,0.35)',
          opacity: 0.6,
        }}
      />
    );
  }

  const hovered = hoverIndex != null ? points[hoverIndex] : null;

  return (
    <Box sx={{ position: 'relative', width, height }}>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        onMouseLeave={() => setHoverIndex(null)}
      >
        {areaD && <path d={areaD} fill={effectiveFill} />}
        <path
          d={pathD}
          fill="none"
          stroke={effectiveStroke}
          strokeWidth={strokeWidth}
          vectorEffect="non-scaling-stroke"
        />
        {points.map((pt, idx) => (
          <circle
            key={idx}
            cx={pt.cx}
            cy={pt.cy}
            r={hoverIndex === idx ? 3.5 : 2.2}
            fill={effectiveStroke}
            onMouseEnter={() => setHoverIndex(idx)}
            style={{ cursor: 'pointer', transition: 'r 0.15s ease' }}
          />
        ))}
      </svg>
      {hovered && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: hovered.cy - 44,
            left: Math.min(Math.max(0, hovered.cx - 24), width - 60),
            px: 1,
            py: 0.5,
            pointerEvents: 'none',
          }}
        >
          <Typography variant="caption">{hovered.x}</Typography>
          <Typography variant="subtitle2">{formatInt(hovered.y)}</Typography>
        </Paper>
      )}
    </Box>
  );
}
